import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  isThisMonth,
  isThisWeek,
  isToday,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";

import { Country } from "@smart/bridge-types-basic";
import { TimePeriod } from "@smart/itops-sb-design-system-dom";

import { GqlSubmissionSummary } from "../../types";

export const isWithinPeriod = (
  period: TimePeriod,
  date: Date,
  country: Country,
  range?: { from?: Date; to?: Date },
): boolean => {
  const now = new Date();
  const weekStartsOn = country === "US" ? 0 : 1;
  const lastWeekInterval = {
    start: startOfWeek(subWeeks(now, 1), { weekStartsOn }),
    end: endOfWeek(subWeeks(now, 1), { weekStartsOn }),
  };

  const lastMonthInterval = {
    start: startOfMonth(subMonths(now, 1)),
    end: endOfMonth(subMonths(now, 1)),
  };

  switch (period) {
    case "today":
      return isToday(date);

    case "thisWeek":
      return isThisWeek(date, { weekStartsOn });

    case "lastWeek":
      return isWithinInterval(date, lastWeekInterval);

    case "thisMonth":
      return isThisMonth(date);

    case "lastMonth":
      return isWithinInterval(date, lastMonthInterval);

    case "customDates":
      if (range?.from && range?.to) {
        return isWithinInterval(date, {
          start: startOfDay(range.from),
          end: endOfDay(range.to),
        });
      }
      return false;

    default:
      return false;
  }
};

export const filterByPeriod = ({
  submissions,
  period,
  country,
  filterByField,
  range,
}: {
  submissions: GqlSubmissionSummary[];
  period: TimePeriod;
  country: Country;
  filterByField: "createdAt" | "updatedAt" | "completedAt";
  range?: { from?: Date; to?: Date };
}) =>
  submissions.filter((sub) => {
    const value = sub.values[filterByField];
    return value
      ? isWithinPeriod(period, parseISO(value), country, range)
      : false;
  });

export const toDateTimeRange = (
  period: TimePeriod,
  country?: Country,
): { from: Date | undefined; to: Date | undefined } => {
  const weekStartsOn = country === "US" ? 0 : 1;
  const now = new Date();
  let from;
  let to;
  switch (period) {
    case "today": {
      from = startOfDay(now);
      to = endOfDay(now);
      break;
    }
    case "thisWeek": {
      from = startOfWeek(now, { weekStartsOn });
      to = endOfWeek(now, { weekStartsOn });
      break;
    }
    case "lastWeek": {
      const lastWeek = subWeeks(now, 1);
      from = startOfWeek(lastWeek, { weekStartsOn });
      to = endOfWeek(lastWeek, { weekStartsOn });
      break;
    }
    case "thisMonth": {
      from = startOfMonth(now);
      to = endOfMonth(now);
      break;
    }
    case "lastMonth": {
      const lastMonth = subMonths(now, 1);
      from = startOfMonth(lastMonth);
      to = endOfMonth(lastMonth);
      break;
    }
    default:
      break;
  }

  return { from, to };
};
